import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
interface Asset {
  name: string;
  value: number;
  cashflow: number;
}
interface AssetsProps {
  onCashflowChange?: (totalCashflow: number) => void;
  onValueChange?: (totalValue: number, totalCashflow: number) => void;
  initialAssets?: Asset[];
}
export const Assets = forwardRef(({
  onCashflowChange,
  onValueChange,
  initialAssets
}: AssetsProps, ref) => {
  // Initialize with 10 blank asset items or use initialAssets if provided
  const [assets, setAssets] = useState<Asset[]>(initialAssets || Array(10).fill(null).map(() => ({
    name: '',
    value: 0,
    cashflow: 0
  })));
  // Expose the assets data to the parent component
  useImperativeHandle(ref, () => ({
    getAssets: () => assets
  }));
  const totalAssets = assets.reduce((sum, asset) => sum + (asset.value || 0), 0);
  const totalCashflow = assets.reduce((sum, asset) => sum + (asset.cashflow || 0), 0);
  // Call the callbacks whenever totals change
  useEffect(() => {
    if (onCashflowChange) {
      onCashflowChange(totalCashflow);
    }
    if (onValueChange) {
      onValueChange(totalAssets, totalCashflow);
    }
  }, [totalAssets, totalCashflow, onCashflowChange, onValueChange]);
  const handleNameChange = (index: number, newName: string) => {
    const updatedAssets = [...assets];
    updatedAssets[index] = {
      ...updatedAssets[index],
      name: newName
    };
    setAssets(updatedAssets);
  };
  const handleValueChange = (index: number, valueStr: string) => {
    // Convert input to number, defaulting to 0 if invalid
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    const updatedAssets = [...assets];
    updatedAssets[index] = {
      ...updatedAssets[index],
      value: numValue
    };
    setAssets(updatedAssets);
  };
  const handleCashflowChange = (index: number, valueStr: string) => {
    // Convert input to number, defaulting to 0 if invalid
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    const updatedAssets = [...assets];
    updatedAssets[index] = {
      ...updatedAssets[index],
      cashflow: numValue
    };
    setAssets(updatedAssets);
  };
  return <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-15">
        <div className="flex justify-between items-center mb-2" data-id="element-16">
          <h2 className="text-xl font-semibold" data-id="element-17">Assets</h2>
          <p className="text-lg font-bold text-green-600" data-id="element-18">
            ${totalAssets.toLocaleString()}
          </p>
        </div>
        {/* Column Headers */}
        <div className="flex justify-between mb-3 text-sm font-medium text-gray-500 border-b pb-2" data-id="element-19">
          <span className="w-1/3" data-id="element-20">Asset Name</span>
          <div className="flex justify-between w-2/3" data-id="element-21">
            <span className="mr-12" data-id="element-22">Value</span>
            <span data-id="element-23">Monthly Cashflow</span>
          </div>
        </div>
        {/* Column Totals */}
        <div className="flex justify-between mb-3 text-sm font-medium" data-id="element-24">
          <span className="w-1/3" data-id="element-25"></span>
          <div className="flex justify-between w-2/3" data-id="element-26">
            <span className="font-bold text-green-600 mr-12" data-id="element-27">
              Total: ${totalAssets.toLocaleString()}
            </span>
            <span className="font-bold text-green-600" data-id="element-28">
              Total: ${totalCashflow.toLocaleString()}/mo
            </span>
          </div>
        </div>
        <div className="space-y-3" data-id="element-29">
          {assets.map((asset, index) => <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-md" data-id="element-30">
              <input type="text" value={asset.name} onChange={e => handleNameChange(index, e.target.value)} placeholder="Asset name" className="text-gray-700 bg-transparent border-b border-transparent focus:border-green-500 focus:outline-none w-1/3" data-id="element-31" />
              <div className="flex items-center" data-id="element-32">
                <span className="mr-1" data-id="element-33">$</span>
                <input type="number" value={asset.value === 0 ? '' : asset.value} onChange={e => handleValueChange(index, e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-green-500 focus:outline-none w-24" data-id="element-34" />
              </div>
              <div className="flex items-center" data-id="element-35">
                <span className="mr-1" data-id="element-36">$</span>
                <input type="number" value={asset.cashflow === 0 ? '' : asset.cashflow} onChange={e => handleCashflowChange(index, e.target.value)} placeholder="0" className="font-medium bg-transparent text-right border-b border-transparent focus:border-green-500 focus:outline-none w-24" data-id="element-37" />
              </div>
            </div>)}
        </div>
      </div>;
});
Assets.displayName = 'Assets';